"use strict";
/*
This file is part of web3.js.

web3.js is free software: you can redistribute it and/or modify
it under the terms of the GNU Lesser General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

web3.js is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Lesser General Public License for more details.

You should have received a copy of the GNU Lesser General Public License
along with web3.js.  If not, see <http://www.gnu.org/licenses/>.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.ErrorFragment = exports.FunctionFragment = exports.ConstructorFragment = exports.EventFragment = exports.Fragment = exports.ParamType = exports.FormatTypes = void 0;
const bignumber_1 = require("@ethersproject/bignumber");
const properties_1 = require("@ethersproject/properties");
const logger_1 = require("@ethersproject/logger");
const _version_js_1 = require("./_version.js");
const logger = new logger_1.Logger(_version_js_1.version);
const _constructorGuard = {};
const ModifiersBytes = { calldata: true, memory: true, storage: true };
const ModifiersNest = { calldata: true, memory: true };
function checkModifier(type, name) {
    if (type === 'bytes' || type === 'string') {
        if (ModifiersBytes[name]) {
            return true;
        }
    }
    else if (type === 'address') {
        if (name === 'payable') {
            return true;
        }
    }
    else if (type.includes('[') || type === 'tuple') {
        if (ModifiersNest[name]) {
            return true;
        }
    }
    if (ModifiersBytes[name] || name === 'payable') {
        logger.throwArgumentError('invalid modifier', 'name', name);
    }
    return false;
}
// @TODO: Make sure that children of an indexed tuple are marked with a null indexed
function parseParamType(param, allowIndexed) {
    const originalParam = param;
    function throwError(i) {
        logger.throwArgumentError(`unexpected character at position ${i}`, 'param', param);
    }
    param = param.replace(/\s/g, ' ');
    function newNode(parent) {
        const node = { type: '', name: '', parent, state: { allowType: true } };
        if (allowIndexed) {
            node.indexed = false;
        }
        return node;
    }
    const parent = { type: '', name: '', state: { allowType: true } };
    let node = parent;
    for (let i = 0; i < param.length; i++) {
        const c = param[i];
        switch (c) {
            case '(':
                if (node.state.allowType && node.type === '') {
                    node.type = 'tuple';
                }
                else if (!node.state.allowParams) {
                    throwError(i);
                }
                node.state.allowType = false;
                node.type = verifyType(node.type);
                node.components = [newNode(node)];
                node = node.components[0];
                break;
            case ')':
                delete node.state;
                if (node.name === 'indexed') {
                    if (!allowIndexed) {
                        throwError(i);
                    }
                    node.indexed = true;
                    node.name = '';
                }
                if (checkModifier(node.type, node.name)) {
                    node.name = '';
                }
                node.type = verifyType(node.type);
                const child = node;
                node = node.parent;
                if (!node) {
                    throwError(i);
                }
                delete child.parent;
                node.state.allowParams = false;
                node.state.allowName = true;
                node.state.allowArray = true;
                break;
            case ',':
                delete node.state;
                if (node.name === 'indexed') {
                    if (!allowIndexed) {
                        throwError(i);
                    }
                    node.indexed = true;
                    node.name = '';
                }
                if (checkModifier(node.type, node.name)) {
                    node.name = '';
                }
                node.type = verifyType(node.type);
                const sibling = newNode(node.parent);
                // { type: "", name: "", parent: node.parent, state: { allowType: true } };
                node.parent.components.push(sibling);
                delete node.parent;
                node = sibling;
                break;
            // Hit a space...
            case ' ':
                // If reading type, the type is done and may read a param or name
                if (node.state.allowType) {
                    if (node.type !== '') {
                        node.type = verifyType(node.type);
                        delete node.state.allowType;
                        node.state.allowName = true;
                        node.state.allowParams = true;
                    }
                }
                // If reading name, the name is done
                if (node.state.allowName) {
                    if (node.name !== '') {
                        if (node.name === 'indexed') {
                            if (!allowIndexed) {
                                throwError(i);
                            }
                            if (node.indexed) {
                                throwError(i);
                            }
                            node.indexed = true;
                            node.name = '';
                        }
                        else if (checkModifier(node.type, node.name)) {
                            node.name = '';
                        }
                        else {
                            node.state.allowName = false;
                        }
                    }
                }
                break;
            case '[':
                if (!node.state.allowArray) {
                    throwError(i);
                }
                node.type += c;
                node.state.allowArray = false;
                node.state.allowName = false;
                node.state.readArray = true;
                break;
            case ']':
                if (!node.state.readArray) {
                    throwError(i);
                }
                node.type += c;
                node.state.readArray = false;
                node.state.allowArray = true;
                node.state.allowName = true;
                break;
            default:
                if (node.state.allowType) {
                    node.type += c;
                    node.state.allowParams = true;
                    node.state.allowArray = true;
                }
                else if (node.state.allowName) {
                    node.name += c;
                    delete node.state.allowArray;
                }
                else if (node.state.readArray) {
                    node.type += c;
                }
                else {
                    throwError(i);
                }
        }
    }
    if (node.parent) {
        logger.throwArgumentError('unexpected eof', 'param', param);
    }
    delete parent.state;
    if (node.name === 'indexed') {
        if (!allowIndexed) {
            throwError(originalParam.length - 7);
        }
        if (node.indexed) {
            throwError(originalParam.length - 7);
        }
        node.indexed = true;
        node.name = '';
    }
    else if (checkModifier(node.type, node.name)) {
        node.name = '';
    }
    parent.type = verifyType(parent.type);
    return parent;
}
function populate(object, params) {
    for (const key in params) {
        (0, properties_1.defineReadOnly)(object, key, params[key]);
    }
}
exports.FormatTypes = Object.freeze({
    // Bare formatting, as is needed for computing a sighash of an event or function
    sighash: 'sighash',
    // Human-Readable with Minimal spacing and without names (compact human-readable)
    minimal: 'minimal',
    // Human-Readable with nice spacing, including all names
    full: 'full',
    // JSON-format a la Hyperion
    json: 'json',
});
const paramTypeArray = new RegExp(/^(.*)\[([0-9]*)\]$/);
class ParamType {
    // The local name of the parameter (of null if unbound)
    name;
    // The fully qualified type (e.g. "address", "tuple(address)", "uint256[3][]"
    type;
    // The base type (e.g. "address", "tuple", "array")
    baseType;
    // Indexable Paramters ONLY (otherwise null)
    indexed;
    // Tuples ONLY: (otherwise null)
    //  - sub-components
    components;
    // Arrays ONLY: (otherwise null)
    //  - length of the array (-1 for dynamic length)
    //  - child type
    arrayLength;
    arrayChildren;
    _isParamType;
    constructor(constructorGuard, params) {
        if (constructorGuard !== _constructorGuard) {
            logger.throwError('use fromString', logger_1.Logger.errors.UNSUPPORTED_OPERATION, {
                operation: 'new ParamType()',
            });
        }
        populate(this, params);
        const match = this.type.match(paramTypeArray);
        if (match) {
            populate(this, {
                arrayLength: parseInt(match[2] || '-1'),
                arrayChildren: ParamType.fromObject({
                    type: match[1],
                    components: this.components,
                }),
                baseType: 'array',
            });
        }
        else {
            populate(this, {
                arrayLength: null,
                arrayChildren: null,
                baseType: this.components != null ? 'tuple' : this.type,
            });
        }
        this._isParamType = true;
        Object.freeze(this);
    }
    // Format the parameter fragment
    //   - sighash: "(uint256,address)"
    //   - minimal: "tuple(uint256,address) indexed"
    //   - full:    "tuple(uint256 foo, address bar) indexed baz"
    format(format) {
        if (!format) {
            format = exports.FormatTypes.sighash;
        }
        if (!exports.FormatTypes[format]) {
            logger.throwArgumentError('invalid format type', 'format', format);
        }
        if (format === exports.FormatTypes.json) {
            const result = {
                type: this.baseType === 'tuple' ? 'tuple' : this.type,
                name: this.name || undefined,
            };
            if (typeof this.indexed === 'boolean') {
                result.indexed = this.indexed;
            }
            if (this.components) {
                result.components = this.components.map(comp => JSON.parse(comp.format(format)));
            }
            return JSON.stringify(result);
        }
        let result = '';
        // Array
        if (this.baseType === 'array') {
            result += this.arrayChildren.format(format);
            result += `[${this.arrayLength < 0 ? '' : String(this.arrayLength)}]`;
        }
        else if (this.baseType === 'tuple') {
            if (format !== exports.FormatTypes.sighash) {
                result += this.type;
            }
            result += `(${this.components
                .map(comp => comp.format(format))
                .join(format === exports.FormatTypes.full ? ', ' : ',')})`;
        }
        else {
            result += this.type;
        }
        if (format !== exports.FormatTypes.sighash) {
            if (this.indexed) {
                result += ' indexed';
            }
            if (format === exports.FormatTypes.full && this.name) {
                result += ` ${this.name}`;
            }
        }
        return result;
    }
    static from(value, allowIndexed) {
        if (typeof value === 'string') {
            return ParamType.fromString(value, allowIndexed);
        }
        return ParamType.fromObject(value);
    }
    static fromObject(value) {
        if (ParamType.isParamType(value)) {
            return value;
        }
        return new ParamType(_constructorGuard, {
            name: value.name || null,
            type: verifyType(value.type),
            indexed: value.indexed == null ? null : !!value.indexed,
            components: value.components ? value.components.map(ParamType.fromObject) : null,
        });
    }
    static fromString(value, allowIndexed) {
        function ParamTypify(node) {
            return ParamType.fromObject({
                name: node.name,
                type: node.type,
                indexed: node.indexed,
                components: node.components,
            });
        }
        return ParamTypify(parseParamType(value, !!allowIndexed));
    }
    static isParamType(value) {
        return !!(value != null && value._isParamType);
    }
}
exports.ParamType = ParamType;
function parseParams(value, allowIndex) {
    return splitNesting(value).map(param => ParamType.fromString(param, allowIndex));
}
class Fragment {
    type;
    name;
    inputs;
    _isFragment;
    constructor(constructorGuard, params) {
        if (constructorGuard !== _constructorGuard) {
            logger.throwError('use a static from method', logger_1.Logger.errors.UNSUPPORTED_OPERATION, {
                operation: 'new Fragment()',
            });
        }
        populate(this, params);
        this._isFragment = true;
        Object.freeze(this);
    }
    static from(value) {
        if (Fragment.isFragment(value)) {
            return value;
        }
        if (typeof value === 'string') {
            return Fragment.fromString(value);
        }
        return Fragment.fromObject(value);
    }
    static fromObject(value) {
        if (Fragment.isFragment(value)) {
            return value;
        }
        switch (value.type) {
            case 'function':
                return FunctionFragment.fromObject(value);
            case 'event':
                return EventFragment.fromObject(value);
            case 'constructor':
                return ConstructorFragment.fromObject(value);
            case 'error':
                return ErrorFragment.fromObject(value);
            case 'fallback':
            case 'receive':
                // @TODO: Something? Maybe return a FunctionFragment? A custom DefaultFunctionFragment?
                return null;
        }
        return logger.throwArgumentError('invalid fragment object', 'value', value);
    }
    static fromString(value) {
        // Make sure the "returns" is surrounded by a space and all whitespace is exactly one space
        value = value.replace(/\s/g, ' ');
        value = value.replace(/\(/g, ' (').replace(/\)/g, ') ').replace(/\s+/g, ' ');
        value = value.trim();
        if (value.split(' ')[0] === 'event') {
            return EventFragment.fromString(value.substring(5).trim());
        }
        if (value.split(' ')[0] === 'function') {
            return FunctionFragment.fromString(value.substring(8).trim());
        }
        if (value.split('(')[0].trim() === 'constructor') {
            return ConstructorFragment.fromString(value.trim());
        }
        if (value.split(' ')[0] === 'error') {
            return ErrorFragment.fromString(value.substring(5).trim());
        }
        return logger.throwArgumentError('unsupported fragment', 'value', value);
    }
    static isFragment(value) {
        return !!(value && value._isFragment);
    }
}
exports.Fragment = Fragment;
class EventFragment extends Fragment {
    anonymous;
    format(format) {
        if (!format) {
            format = exports.FormatTypes.sighash;
        }
        if (!exports.FormatTypes[format]) {
            logger.throwArgumentError('invalid format type', 'format', format);
        }
        if (format === exports.FormatTypes.json) {
            return JSON.stringify({
                type: 'event',
                anonymous: this.anonymous,
                name: this.name,
                inputs: this.inputs.map(input => JSON.parse(input.format(format))),
            });
        }
        let result = '';
        if (format !== exports.FormatTypes.sighash) {
            result += 'event ';
        }
        result += `${this.name}(${this.inputs
            .map(input => input.format(format))
            .join(format === exports.FormatTypes.full ? ', ' : ',')}) `;
        if (format !== exports.FormatTypes.sighash) {
            if (this.anonymous) {
                result += 'anonymous ';
            }
        }
        return result.trim();
    }
    static from(value) {
        if (typeof value === 'string') {
            return EventFragment.fromString(value);
        }
        return EventFragment.fromObject(value);
    }
    static fromObject(value) {
        if (EventFragment.isEventFragment(value)) {
            return value;
        }
        if (value.type !== 'event') {
            logger.throwArgumentError('invalid event object', 'value', value);
        }
        const params = {
            name: verifyIdentifier(value.name),
            anonymous: value.anonymous,
            inputs: value.inputs ? value.inputs.map(ParamType.fromObject) : [],
            type: 'event',
        };
        return new EventFragment(_constructorGuard, params);
    }
    static fromString(value) {
        const match = value.match(regexParen);
        if (!match) {
            logger.throwArgumentError('invalid event string', 'value', value);
        }
        let anonymous = false;
        match[3].split(' ').forEach(modifier => {
            switch (modifier.trim()) {
                case 'anonymous':
                    anonymous = true;
                    break;
                case '':
                    break;
                default:
                    logger.warn(`unknown modifier: ${modifier}`);
            }
        });
        return EventFragment.fromObject({
            name: match[1].trim(),
            anonymous,
            inputs: parseParams(match[2], true),
            type: 'event',
        });
    }
    static isEventFragment(value) {
        return value && value._isFragment && value.type === 'event';
    }
}
exports.EventFragment = EventFragment;
function parseGas(value, params) {
    params.gas = null;
    const comps = value.split('@');
    if (comps.length !== 1) {
        if (comps.length > 2) {
            logger.throwArgumentError('invalid human-readable ABI signature', 'value', value);
        }
        if (!comps[1].match(/^[0-9]+$/)) {
            logger.throwArgumentError('invalid human-readable ABI signature gas', 'value', value);
        }
        params.gas = bignumber_1.BigNumber.from(comps[1]);
        return comps[0];
    }
    return value;
}
function parseModifiers(value, params) {
    params.constant = false;
    params.payable = false;
    params.stateMutability = 'nonpayable';
    value.split(' ').forEach(modifier => {
        switch (modifier.trim()) {
            case 'constant':
                params.constant = true;
                break;
            case 'payable':
                params.payable = true;
                params.stateMutability = 'payable';
                break;
            case 'nonpayable':
                params.payable = false;
                params.stateMutability = 'nonpayable';
                break;
            case 'pure':
                params.constant = true;
                params.stateMutability = 'pure';
                break;
            case 'view':
                params.constant = true;
                params.stateMutability = 'view';
                break;
            case 'external':
            case 'public':
            case '':
                break;
            default:
                console.log(`unknown modifier: ${modifier}`);
        }
    });
}
function verifyState(value) {
    const result = {
        constant: false,
        payable: true,
        stateMutability: 'payable',
    };
    if (value.stateMutability != null) {
        result.stateMutability = value.stateMutability;
        // Set (and check things are consistent) the constant property
        result.constant = result.stateMutability === 'view' || result.stateMutability === 'pure';
        if (value.constant != null) {
            if (!!value.constant !== result.constant) {
                logger.throwArgumentError(`cannot have constant function with mutability ${result.stateMutability}`, 'value', value);
            }
        }
        // Set (and check things are consistent) the payable property
        result.payable = result.stateMutability === 'payable';
        if (value.payable != null) {
            if (!!value.payable !== result.payable) {
                logger.throwArgumentError(`cannot have payable function with mutability ${result.stateMutability}`, 'value', value);
            }
        }
    }
    else if (value.payable != null) {
        result.payable = !!value.payable;
        // If payable we can assume non-constant; otherwise we can't assume
        if (value.constant == null && !result.payable && value.type !== 'constructor') {
            logger.throwArgumentError('unable to determine stateMutability', 'value', value);
        }
        result.constant = !!value.constant;
        if (result.constant) {
            result.stateMutability = 'view';
        }
        else {
            result.stateMutability = result.payable ? 'payable' : 'nonpayable';
        }
        if (result.payable && result.constant) {
            logger.throwArgumentError('cannot have constant payable function', 'value', value);
        }
    }
    else if (value.constant != null) {
        result.constant = !!value.constant;
        result.payable = !result.constant;
        result.stateMutability = result.constant ? 'view' : 'payable';
    }
    else if (value.type !== 'constructor') {
        logger.throwArgumentError('unable to determine stateMutability', 'value', value);
    }
    return result;
}
class ConstructorFragment extends Fragment {
    stateMutability;
    payable;
    gas;
    format(format) {
        if (!format) {
            format = exports.FormatTypes.sighash;
        }
        if (!exports.FormatTypes[format]) {
            logger.throwArgumentError('invalid format type', 'format', format);
        }
        if (format === exports.FormatTypes.json) {
            return JSON.stringify({
                type: 'constructor',
                stateMutability: this.stateMutability !== 'nonpayable' ? this.stateMutability : undefined,
                payable: this.payable,
                gas: this.gas ? this.gas.toNumber() : undefined,
                inputs: this.inputs.map(input => JSON.parse(input.format(format))),
            });
        }
        if (format === exports.FormatTypes.sighash) {
            logger.throwError('cannot format a constructor for sighash', logger_1.Logger.errors.UNSUPPORTED_OPERATION, {
                operation: 'format(sighash)',
            });
        }
        let result = `constructor(${this.inputs
            .map(input => input.format(format))
            .join(format === exports.FormatTypes.full ? ', ' : ',')}) `;
        if (this.stateMutability && this.stateMutability !== 'nonpayable') {
            result += `${this.stateMutability} `;
        }
        return result.trim();
    }
    static from(value) {
        if (typeof value === 'string') {
            return ConstructorFragment.fromString(value);
        }
        return ConstructorFragment.fromObject(value);
    }
    static fromObject(value) {
        if (ConstructorFragment.isConstructorFragment(value)) {
            return value;
        }
        if (value.type !== 'constructor') {
            logger.throwArgumentError('invalid constructor object', 'value', value);
        }
        const state = verifyState(value);
        if (state.constant) {
            logger.throwArgumentError('constructor cannot be constant', 'value', value);
        }
        const params = {
            name: null,
            type: value.type,
            inputs: value.inputs ? value.inputs.map(ParamType.fromObject) : [],
            payable: state.payable,
            stateMutability: state.stateMutability,
            gas: value.gas ? bignumber_1.BigNumber.from(value.gas) : null,
        };
        return new ConstructorFragment(_constructorGuard, params);
    }
    static fromString(value) {
        const params = { type: 'constructor' };
        value = parseGas(value, params);
        const parens = value.match(regexParen);
        if (!parens || parens[1].trim() !== 'constructor') {
            logger.throwArgumentError('invalid constructor string', 'value', value);
        }
        params.inputs = parseParams(parens[2].trim(), false);
        parseModifiers(parens[3].trim(), params);
        return ConstructorFragment.fromObject(params);
    }
    static isConstructorFragment(value) {
        return value && value._isFragment && value.type === 'constructor';
    }
}
exports.ConstructorFragment = ConstructorFragment;
class FunctionFragment extends ConstructorFragment {
    constant;
    outputs;
    format(format) {
        if (!format) {
            format = exports.FormatTypes.sighash;
        }
        if (!exports.FormatTypes[format]) {
            logger.throwArgumentError('invalid format type', 'format', format);
        }
        if (format === exports.FormatTypes.json) {
            return JSON.stringify({
                type: 'function',
                name: this.name,
                constant: this.constant,
                stateMutability: this.stateMutability !== 'nonpayable' ? this.stateMutability : undefined,
                payable: this.payable,
                gas: this.gas ? this.gas.toNumber() : undefined,
                inputs: this.inputs.map(input => JSON.parse(input.format(format))),
                outputs: this.outputs.map(output => JSON.parse(output.format(format))),
            });
        }
        let result = '';
        if (format !== exports.FormatTypes.sighash) {
            result += 'function ';
        }
        result += `${this.name}(${this.inputs
            .map(input => input.format(format))
            .join(format === exports.FormatTypes.full ? ', ' : ',')}) `;
        if (format !== exports.FormatTypes.sighash) {
            if (this.stateMutability) {
                if (this.stateMutability !== 'nonpayable') {
                    result += `${this.stateMutability} `;
                }
            }
            else if (this.constant) {
                result += 'view ';
            }
            if (this.outputs && this.outputs.length) {
                result += `returns (${this.outputs
                    .map(output => output.format(format))
                    .join(', ')}) `;
            }
            if (this.gas != null) {
                result += `@${this.gas.toString()} `;
            }
        }
        return result.trim();
    }
    static from(value) {
        if (typeof value === 'string') {
            return FunctionFragment.fromString(value);
        }
        return FunctionFragment.fromObject(value);
    }
    static fromObject(value) {
        if (FunctionFragment.isFunctionFragment(value)) {
            return value;
        }
        if (value.type !== 'function') {
            logger.throwArgumentError('invalid function object', 'value', value);
        }
        const state = verifyState(value);
        const params = {
            type: value.type,
            name: verifyIdentifier(value.name),
            constant: state.constant,
            inputs: value.inputs ? value.inputs.map(ParamType.fromObject) : [],
            outputs: value.outputs ? value.outputs.map(ParamType.fromObject) : [],
            payable: state.payable,
            stateMutability: state.stateMutability,
            gas: value.gas ? bignumber_1.BigNumber.from(value.gas) : null,
        };
        return new FunctionFragment(_constructorGuard, params);
    }
    static fromString(value) {
        const params = { type: 'function' };
        value = parseGas(value, params);
        const comps = value.split(' returns ');
        if (comps.length > 2) {
            logger.throwArgumentError('invalid function string', 'value', value);
        }
        const parens = comps[0].match(regexParen);
        if (!parens) {
            logger.throwArgumentError('invalid function signature', 'value', value);
        }
        params.name = parens[1].trim();
        if (params.name) {
            verifyIdentifier(params.name);
        }
        params.inputs = parseParams(parens[2], false);
        parseModifiers(parens[3].trim(), params);
        // We have outputs
        if (comps.length > 1) {
            const returns = comps[1].match(regexParen);
            if (returns[1].trim() != '' || returns[3].trim() != '') {
                logger.throwArgumentError('unexpected tokens', 'value', value);
            }
            params.outputs = parseParams(returns[2], false);
        }
        else {
            params.outputs = [];
        }
        return FunctionFragment.fromObject(params);
    }
    static isFunctionFragment(value) {
        return value && value._isFragment && value.type === 'function';
    }
}
exports.FunctionFragment = FunctionFragment;
// export class StructFragment extends Fragment {
// }
function checkForbidden(fragment) {
    const sig = fragment.format();
    if (sig === 'Error(string)' || sig === 'Panic(uint256)') {
        logger.throwArgumentError(`cannot specify user defined ${sig} error`, 'fragment', fragment);
    }
    return fragment;
}
class ErrorFragment extends Fragment {
    format(format) {
        if (!format) {
            format = exports.FormatTypes.sighash;
        }
        if (!exports.FormatTypes[format]) {
            logger.throwArgumentError('invalid format type', 'format', format);
        }
        if (format === exports.FormatTypes.json) {
            return JSON.stringify({
                type: 'error',
                name: this.name,
                inputs: this.inputs.map(input => JSON.parse(input.format(format))),
            });
        }
        let result = '';
        if (format !== exports.FormatTypes.sighash) {
            result += 'error ';
        }
        result += `${this.name}(${this.inputs
            .map(input => input.format(format))
            .join(format === exports.FormatTypes.full ? ', ' : ',')}) `;
        return result.trim();
    }
    static from(value) {
        if (typeof value === 'string') {
            return ErrorFragment.fromString(value);
        }
        return ErrorFragment.fromObject(value);
    }
    static fromObject(value) {
        if (ErrorFragment.isErrorFragment(value)) {
            return value;
        }
        if (value.type !== 'error') {
            logger.throwArgumentError('invalid error object', 'value', value);
        }
        const params = {
            type: value.type,
            name: verifyIdentifier(value.name),
            inputs: value.inputs ? value.inputs.map(ParamType.fromObject) : [],
        };
        return checkForbidden(new ErrorFragment(_constructorGuard, params));
    }
    static fromString(value) {
        const params = { type: 'error' };
        const parens = value.match(regexParen);
        if (!parens) {
            logger.throwArgumentError('invalid error signature', 'value', value);
        }
        params.name = parens[1].trim();
        if (params.name) {
            verifyIdentifier(params.name);
        }
        params.inputs = parseParams(parens[2], false);
        return checkForbidden(ErrorFragment.fromObject(params));
    }
    static isErrorFragment(value) {
        return value && value._isFragment && value.type === 'error';
    }
}
exports.ErrorFragment = ErrorFragment;
function verifyType(type) {
    // These need to be transformed to their full description
    if (type.match(/^uint($|[^1-9])/)) {
        type = `uint256${type.substring(4)}`;
    }
    else if (type.match(/^int($|[^1-9])/)) {
        type = `int256${type.substring(3)}`;
    }
    // @TODO: more verification
    return type;
}
// See: https://github.com/ethereum/solidity/blob/1f8f1a3db93a548d0555e3e14cfc55a10e25b60e/docs/grammar/SolidityLexer.g4#L234
const regexIdentifier = new RegExp('^[a-zA-Z$_][a-zA-Z0-9$_]*$');
function verifyIdentifier(value) {
    if (!value || !value.match(regexIdentifier)) {
        logger.throwArgumentError(`invalid identifier "${value}"`, 'value', value);
    }
    return value;
}
const regexParen = new RegExp('^([^)(]*)\\((.*)\\)([^)(]*)$');
function splitNesting(value) {
    value = value.trim();
    const result = [];
    let accum = '';
    let depth = 0;
    for (let offset = 0; offset < value.length; offset++) {
        const c = value[offset];
        if (c === ',' && depth === 0) {
            result.push(accum);
            accum = '';
        }
        else {
            accum += c;
            if (c === '(') {
                depth++;
            }
            else if (c === ')') {
                depth--;
                if (depth === -1) {
                    logger.throwArgumentError('unbalanced parenthesis', 'value', value);
                }
            }
        }
    }
    if (accum) {
        result.push(accum);
    }
    return result;
}
//# sourceMappingURL=fragments.js.map